import type { JudgeConfig } from '@meridian-ssa/shared';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { EvalLayout } from '@/components/eval/EvalLayout';
import { JudgeConfigForm } from '@/components/judge-configs/JudgeConfigForm';
import { apiRequest } from '@/config';
import { logDebug, logError } from '@/lib/logger';

interface DetailedStats {
  total_seed_votes: number;
  total_llm_votes: number;
  coverage_percent: number;
  recent_votes: number;
  judge_config: JudgeConfig;
}

interface ProcessingJob {
  id: string;
  status: string;
  processed: number;
  total: number;
  skipped: number;
  progress_percent: number;
}

export default function LLMJudgeAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [judgeConfigs, setJudgeConfigs] = useState<JudgeConfig[]>([]);
  const [selectedJudgeConfigId, setSelectedJudgeConfigId] =
    useState<string>('');
  const [viewMode, setViewMode] = useState<'view' | 'create'>('view');

  const [stats, setStats] = useState<DetailedStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [activeJob, setActiveJob] = useState<ProcessingJob | null>(null);
  const [voteLimit, setVoteLimit] = useState<string>('');

  useEffect(() => {
    loadJudgeConfigs();
  }, []);

  useEffect(() => {
    if (selectedJudgeConfigId && viewMode === 'view') {
      loadStats();

      // Poll for updates if there's an active job
      const interval = setInterval(() => {
        if (activeJob && activeJob.status === 'running') {
          checkJobStatus();
        }
        loadStats();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [selectedJudgeConfigId, activeJob?.id, viewMode]);

  async function loadJudgeConfigs() {
    try {
      const response: { configs: JudgeConfig[] } =
        await apiRequest('/api/judge-configs');
      setJudgeConfigs(response.configs);

      // Check URL params for selected config
      const judgeConfigId = searchParams.get('judge_config_id') || '';

      if (
        judgeConfigId &&
        response.configs.some((c: JudgeConfig) => c.id === judgeConfigId)
      ) {
        // URL param exists and is valid, use it
        setSelectedJudgeConfigId(judgeConfigId);
      } else if (response.configs.length > 0) {
        // Auto-select first config and update URL
        const firstConfigId = response.configs[0].id;
        setSelectedJudgeConfigId(firstConfigId);
        setSearchParams({ judge_config_id: firstConfigId });
      }

      setLoading(false);
    } catch (e: any) {
      setError(e.message);
      setLoading(false);
    }
  }

  async function loadStats() {
    if (!selectedJudgeConfigId) return;

    try {
      const data = await apiRequest(
        `/api/llm-judge/stats?judge_config_id=${selectedJudgeConfigId}`
      );
      setStats(data);
      setLoading(false);
    } catch (e: any) {
      setError(e.message);
      setLoading(false);
    }
  }

  function handleJudgeSelect(configId: string) {
    setSelectedJudgeConfigId(configId);
    logDebug('Setting judge config');
    setSearchParams({ judge_config_id: configId });
    setViewMode('view');
    setStats(null);
  }

  function handleCreateNew() {
    setViewMode('create');
    setSelectedJudgeConfigId('');
    setSearchParams({});
  }

  function handleCreateSuccess(newConfig: JudgeConfig) {
    setJudgeConfigs([...judgeConfigs, newConfig]);
    setSelectedJudgeConfigId(newConfig.id);
    setSearchParams({ judge_config_id: newConfig.id });
    setViewMode('view');
  }

  function handleCreateCancel() {
    if (judgeConfigs.length > 0) {
      const firstConfig = judgeConfigs[0];
      setSelectedJudgeConfigId(firstConfig.id);
      setSearchParams({ judge_config_id: firstConfig.id });
      setViewMode('view');
    }
  }

  async function startProcessing() {
    if (!selectedJudgeConfigId) {
      alert('Please select a judge config first');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('judge_config_id', selectedJudgeConfigId);

      const limitNum = parseInt(voteLimit);
      if (!isNaN(limitNum) && limitNum > 0) {
        params.set('limit', limitNum.toString());
      }

      const data = await apiRequest(
        `/api/llm-judge/process?${params.toString()}`,
        {
          method: 'POST',
        }
      );

      if (data.total_votes === 0) {
        alert('No pending votes to process');
        return;
      }

      setActiveJob({
        id: data.job_id,
        status: 'running',
        processed: 0,
        total: data.total_votes,
        skipped: 0,
        progress_percent: 0,
      });

      const limitInfo =
        limitNum && limitNum > 0
          ? ` (limited to ${limitNum} earliest votes)`
          : '';
      alert(`Started processing ${data.total_votes} votes${limitInfo}`);
    } catch (e: any) {
      setError(e.message);
      alert(`Error: ${e.message}`);
    } finally {
      setProcessing(false);
    }
  }

  async function checkJobStatus() {
    if (!activeJob) return;

    try {
      const data = await apiRequest(`/api/llm-judge/status/${activeJob.id}`);
      setActiveJob(data.job);

      if (data.job.status === 'completed') {
        loadStats();
      }
    } catch (e: any) {
      logError('Error checking job status', e);
    }
  }

  async function handleDelete() {
    if (!selectedJudgeConfigId) return;

    const config = judgeConfigs.find(c => c.id === selectedJudgeConfigId);
    if (!config) return;

    if (
      !confirm(
        `Are you sure you want to delete "${config.name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await apiRequest(`/api/judge-configs/${selectedJudgeConfigId}`, {
        method: 'DELETE',
      });

      // Remove from list
      const updatedConfigs = judgeConfigs.filter(
        c => c.id !== selectedJudgeConfigId
      );
      setJudgeConfigs(updatedConfigs);

      // Select first remaining config or switch to create mode
      if (updatedConfigs.length > 0) {
        handleJudgeSelect(updatedConfigs[0].id);
      } else {
        handleCreateNew();
      }

      alert('Judge config deleted successfully');
    } catch (e: any) {
      alert(`Error deleting config: ${e.message}`);
    }
  }

  function getProviderBadgeColor(provider: string) {
    switch (provider) {
      case 'openai':
        return 'bg-green-100 text-green-800';
      case 'anthropic':
        return 'bg-purple-100 text-purple-800';
      case 'xai':
        return 'bg-blue-100 text-blue-800';
      case 'gemini':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  if (loading && judgeConfigs.length === 0) {
    return (
      <EvalLayout>
        <div className="text-center text-muted py-16">Loading...</div>
      </EvalLayout>
    );
  }

  const pendingCount = stats
    ? stats.total_seed_votes - stats.total_llm_votes
    : 0;

  return (
    <EvalLayout>
      <div
        className="flex gap-6 -mt-12 -mx-16"
        style={{ minHeight: 'calc(100vh - 4rem)' }}
      >
        {/* Sidebar */}
        <aside className="w-80 bg-white border-r border-[#E8E3DD] p-6 flex flex-col">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-text mb-4">
              LLM Judge Admin
            </h1>
            <button
              onClick={handleCreateNew}
              className={`w-full px-4 py-2 rounded font-medium transition-colors ${
                viewMode === 'create'
                  ? 'bg-accent text-white'
                  : 'bg-[#F5F1EE] text-text hover:bg-[#EFEBE5]'
              }`}
            >
              + Create New Judge
            </button>
          </div>

          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            <div className="space-y-1">
              {judgeConfigs.map(config => {
                const isSelected =
                  selectedJudgeConfigId === config.id && viewMode === 'view';
                return (
                  <button
                    key={config.id}
                    onClick={() => handleJudgeSelect(config.id)}
                    className={`w-full text-left px-3 py-3 rounded transition-colors ${
                      isSelected
                        ? 'bg-accent text-white'
                        : 'bg-[#F5F1EE] hover:bg-[#EFEBE5]'
                    }`}
                  >
                    <div
                      className={`font-medium mb-1 ${isSelected ? 'text-white' : 'text-text'}`}
                    >
                      {config.name}
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded capitalize ${
                          isSelected
                            ? 'bg-white/20 text-white'
                            : getProviderBadgeColor(config.provider)
                        }`}
                      >
                        {config.provider}
                      </span>
                      <span
                        className={`text-xs truncate ${isSelected ? 'text-white/80' : 'text-text-light'}`}
                      >
                        {config.api_model}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {judgeConfigs.length === 0 && viewMode === 'view' && (
            <div className="text-center text-text-light text-sm py-8">
              No judge configs yet. Click "Create New Judge" to get started.
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8 overflow-y-auto">
          {viewMode === 'create' ? (
            <div>
              <h2 className="text-2xl font-semibold text-text mb-6">
                Create Judge Config
              </h2>
              <JudgeConfigForm
                onSuccess={handleCreateSuccess}
                onCancel={handleCreateCancel}
              />
            </div>
          ) : selectedJudgeConfigId && stats ? (
            <div className="space-y-6">
              {/* Judge Details */}
              <div className="p-6 bg-white border border-[#D1C9C4] rounded-lg">
                <h2 className="text-xl font-semibold text-text mb-4">
                  Judge Configuration
                </h2>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-text-light mb-1">Provider</div>
                    <div className="text-lg font-medium text-text capitalize">
                      {stats.judge_config.provider}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-text-light mb-1">Model</div>
                    <div className="text-lg font-medium text-text">
                      {stats.judge_config.api_model}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-text-light mb-1">
                      Temperature
                    </div>
                    <div className="text-lg font-medium text-text">
                      {stats.judge_config.temperature}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-text-light mb-1">
                      Max Tokens
                    </div>
                    <div className="text-lg font-medium text-text">
                      {stats.judge_config.max_tokens}
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Job */}
              {activeJob && activeJob.status === 'running' && (
                <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-text mb-3">
                    Processing Job
                  </h3>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-text-light">Progress</span>
                      <span className="text-text font-medium">
                        {activeJob.processed} / {activeJob.total} (
                        {activeJob.progress_percent}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-accent h-2 rounded-full transition-all"
                        style={{ width: `${activeJob.progress_percent}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-text-light">Processed</div>
                      <div className="text-lg font-medium text-[#10B981]">
                        {activeJob.processed}
                      </div>
                    </div>
                    <div>
                      <div className="text-text-light">Skipped</div>
                      <div className="text-lg font-medium text-[#F59E0B]">
                        {activeJob.skipped}
                      </div>
                    </div>
                    <div>
                      <div className="text-text-light">Remaining</div>
                      <div className="text-lg font-medium text-[#6B7280]">
                        {activeJob.total - activeJob.processed}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Coverage & Processing */}
              <div className="p-6 bg-white border border-[#D1C9C4] rounded-lg">
                <div className="mb-6">
                  <div className="text-sm text-text-light mb-1">Coverage</div>
                  <div className="text-4xl font-bold text-accent mb-2">
                    {stats.coverage_percent.toFixed(1)}%
                  </div>
                  <div className="text-sm text-text-light mb-4">
                    {stats.total_llm_votes.toLocaleString()} /{' '}
                    {stats.total_seed_votes.toLocaleString()} votes
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-accent h-2 rounded-full transition-all"
                      style={{ width: `${stats.coverage_percent}%` }}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm text-text-light mb-2">
                    Limit (optional - earliest N votes)
                  </label>
                  <input
                    type="number"
                    value={voteLimit}
                    onChange={e => setVoteLimit(e.target.value)}
                    placeholder="Leave empty for all"
                    min="1"
                    className="w-full px-3 py-2 border border-[#D1C9C4] rounded text-sm focus:outline-none focus:border-accent"
                  />
                </div>

                <button
                  onClick={startProcessing}
                  disabled={processing || pendingCount === 0}
                  className="w-full px-4 py-2 bg-accent text-white rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {processing
                    ? 'Starting...'
                    : voteLimit && parseInt(voteLimit) > 0
                      ? `Process ${Math.min(parseInt(voteLimit), pendingCount).toLocaleString()} Earliest`
                      : `Process All ${pendingCount.toLocaleString()}`}
                </button>
              </div>

              {/* Recent Activity */}
              <div className="p-6 bg-white border border-[#D1C9C4] rounded-lg">
                <h3 className="text-lg font-semibold text-text mb-2">
                  Recent Activity
                </h3>
                <div className="text-sm text-text-light">
                  {stats.recent_votes || 0} votes processed in the last 24 hours
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <Link
                  to={`/leaderboard?source=llm-seed&judge_config_id=${selectedJudgeConfigId}`}
                  className="px-6 py-3 bg-accent text-white rounded hover:opacity-90 no-underline font-medium"
                >
                  View Leaderboard
                </Link>
                <button
                  onClick={loadStats}
                  className="px-6 py-3 bg-white border border-[#D1C9C4] text-text rounded hover:bg-[#F5F1EE] font-medium"
                >
                  Refresh Stats
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-3 bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 font-medium ml-auto"
                >
                  Delete Config
                </button>
              </div>
            </div>
          ) : viewMode === 'view' && !selectedJudgeConfigId ? (
            <div className="text-center text-text-light py-16">
              <p className="mb-4">No judge config selected</p>
              <button
                onClick={handleCreateNew}
                className="px-6 py-2 bg-accent text-white rounded hover:opacity-90"
              >
                Create Your First Judge
              </button>
            </div>
          ) : (
            <div className="text-center text-text-light py-16">
              Loading stats...
            </div>
          )}
        </main>
      </div>
    </EvalLayout>
  );
}
